import React, { createContext, ReactNode, useContext, useEffect, useMemo, useRef, useState } from 'react';

interface PageContextProps {
    activeSectionId: string | null;
    setActiveSectionId: React.Dispatch<React.SetStateAction<string | null>>;
    setActiveSectionIdPauseScrollSpy: (id: string) => void;
    generalInformationLoaded: boolean;
    setGeneralInformationLoaded: React.Dispatch<React.SetStateAction<boolean>>;
    sectionsLoaded: boolean;
    setSectionsLoaded: React.Dispatch<React.SetStateAction<boolean>>;
    pageLoaded: boolean;
    setPageLoaded: React.Dispatch<React.SetStateAction<boolean>>;
    resetLoadedContext: () => void;
    isScrollSpyPausedRef: React.MutableRefObject<boolean>;
}

export const PageContext = createContext<PageContextProps | undefined>(undefined);

interface PageContextProviderProps {
    children: React.ReactNode;
}

export const PageContextProvider: React.FC<PageContextProviderProps> = ({ children }): ReactNode => {
    const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
    const [generalInformationLoaded, setGeneralInformationLoaded] = useState(false);
    const [sectionsLoaded, setSectionsLoaded] = useState(false);
    const [pageLoaded, setPageLoaded] = useState(false);
    const isScrollSpyPausedRef = useRef(false);

    useEffect(() => {
        if (generalInformationLoaded && sectionsLoaded) {
            setPageLoaded(true);
        }
    }, [generalInformationLoaded, sectionsLoaded]);

    const contextValue = useMemo(
        () => ({
            activeSectionId,
            setActiveSectionId,
            setActiveSectionIdPauseScrollSpy: (id: string, pause: number = 1000) => {
                isScrollSpyPausedRef.current = true;
                setActiveSectionId(id);
                setTimeout(() => {
                    isScrollSpyPausedRef.current = false;
                }, pause);
            },
            generalInformationLoaded,
            setGeneralInformationLoaded,
            sectionsLoaded,
            setSectionsLoaded,
            pageLoaded,
            setPageLoaded,
            resetLoadedContext: (): void => {
                setGeneralInformationLoaded(false);
                setSectionsLoaded(false);
                setPageLoaded(false);
            },
            isScrollSpyPausedRef,
        }),
        [activeSectionId, generalInformationLoaded, sectionsLoaded, pageLoaded]
    );

    return <PageContext.Provider value={contextValue}>{children}</PageContext.Provider>;
};

export const usePageContext = (): PageContextProps => {
    const context = useContext(PageContext);

    if (!context) {
        throw new Error('usePageContext used while not within PageContextProvider.');
    }

    return context;
};
